"use client";

import { useState, type MouseEvent } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ArrowDown, Loader2 } from "lucide-react";
import { CardsList } from "@/lib/interfaces/cards";
import { CardService } from "@/lib/services/cardService";
import CategoryCard from "./CategoryCard";

interface LoadMoreClientProps {
  initialHasMore: boolean;
  /** Page the server rendered (from ?page=, default 1). */
  startPage: number;
  totalPages?: number;
  slug: string;
  activeFilterIds: string[];
  isLatest: boolean;
  isStaffPick: boolean;
  sortBy: string;
  sortOrder: string;
}

/**
 * "Load more" as progressive enhancement: the control is a real link to
 * ?page=N+1 (so crawlers and AI agents can reach every design), while clicks
 * in the browser are intercepted to append the next page in place.
 */
export default function LoadMoreClient({
  initialHasMore,
  startPage,
  totalPages,
  slug,
  activeFilterIds,
  isLatest,
  isStaffPick,
  sortBy,
  sortOrder,
}: LoadMoreClientProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [cards, setCards] = useState<CardsList[]>([]);
  const [page, setPage] = useState(startPage);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [isLoading, setIsLoading] = useState(false);

  const nextHref = (() => {
    const params = new URLSearchParams(searchParams?.toString());
    params.set("page", String(page + 1));
    return `${pathname}?${params.toString()}`;
  })();

  const loadMore = async (e: MouseEvent<HTMLAnchorElement>) => {
    // Let modified clicks (new tab etc.) follow the real link.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const nextPage = page + 1;

    try {
      const res = await CardService.getCardsBySlug(slug, {
        filters: activeFilterIds.join(","),
        is_latest: isLatest,
        is_staff_pick: isStaffPick,
        sort_by: sortBy as "position" | "created_at",
        sort_order: sortOrder as "asc" | "desc",
        page: nextPage,
      });

      if (res?.response_status && res?.response_data) {
        setCards((prev) => [...prev, ...res.response_data]);
        setHasMore(res.pagination?.next !== null && res.response_data.length > 0);
        setPage(nextPage);
        // Reflect the loaded page in the address bar (no navigation or refetch),
        // replacing rather than pushing so Back still leaves the category.
        window.history.replaceState(window.history.state, "", nextHref);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Failed to load more cards:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {cards.length > 0 && (
        <ul className="mt-5 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 xl:grid-cols-4">
          {cards.map((card, index) => (
            <CategoryCard key={card.id} card={card} index={index + 4} />
          ))}
        </ul>
      )}

      {hasMore && (
        <nav aria-label="More designs" className="mt-10 flex flex-col items-center gap-2">
          <Link
            href={nextHref}
            onClick={loadMore}
            prefetch={false}
            rel="next"
            aria-busy={isLoading}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ArrowDown className="h-4 w-4" aria-hidden="true" />}
            {isLoading ? "Loading designs…" : "Load more designs"}
          </Link>
          {totalPages ? <p className="text-xs text-gray-600">Page {page} of {totalPages}</p> : null}
        </nav>
      )}
    </>
  );
}
